import { parseEmailList } from "@/lib/email/utils/parse-recipients";
import { HotelChainRep } from "@/lib/types/hotel";

export interface ChainRepFallbackRecipients {
  to: string[];
  cc: string[];
}

/**
 * Splits chain reps into a `TO` fallback and a `CC` remainder, for use when a
 * venue has no email of its own. Prefers reps flagged `direct_recipient`; if
 * none are flagged, every rep with an email becomes `TO` so the venue is never
 * silently skipped. Reps chosen for `TO` never also appear in `cc`.
 *
 * When no chain rep is left because the chain's only rep was the global one,
 * dropped for the enterprise's own (`applyEnterpriseRepPrecedence`), the first
 * of those replacement reps becomes `TO` instead, so the venue is still reached
 * through the contact that replaced the global rep.
 *
 * @param chainReps - The chain reps resolved for the venue's chain.
 * @param replacementRepEmails - The reps that replaced a dropped global chain rep, used as `TO`
 *   only when `chainReps` yields none.
 * @returns Deduped, validated `to` and `cc` address lists (both may be empty).
 */
export function selectChainRepFallbackRecipients(
  chainReps: HotelChainRep[],
  replacementRepEmails: string[] = []
): ChainRepFallbackRecipients {
  const directReps = chainReps.filter((rep) => rep.direct_recipient && rep.email);
  const toReps = directReps.length > 0 ? directReps : chainReps.filter((rep) => rep.email);

  const chainTo = parseEmailList(toReps.map((rep) => rep.email).join(","));
  const to = chainTo.length > 0 ? chainTo : parseEmailList(replacementRepEmails.join(",")).slice(0, 1);
  const toLookup = new Set(to.map((email) => email.toLowerCase()));
  const cc = parseEmailList(chainReps.map((rep) => rep.email).join(",")).filter(
    (email) => !toLookup.has(email.toLowerCase())
  );

  return { to, cc };
}
